import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, Search, Shield } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AdminOnlyEmptyState } from '@/components/auth/admin-only-empty-state';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { HelpSupportButton } from '@/components/help-support';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ManageUserCompaniesDialog } from '@/components/users/manage-user-companies-dialog';
import { ManageUserIntegrationsDialog } from '@/components/users/manage-user-integrations-dialog';
import { UsersTable } from '@/components/users/users-table';
import { ViewUserDialog } from '@/components/users/view-user-dialog';
import { useApi } from '@/hooks/useApi';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { type UsersListParams, useUpdateUser, useUsersListPaginated } from '@/hooks/useUsersData';
import { getPageNumbers } from '@/lib/pagination-utils';
import {
  DEFAULT_PERMISSIONS,
  ROLE_DESCRIPTIONS,
  type User,
  type UserFilters,
  type UserPermissions,
  type UserRole,
  type UserStatus,
} from '@/types/user';

// Custom Role Interface
interface CustomRole {
  id: string;
  name: string;
  description: string;
  permissions: UserPermissions;
  userCount: number;
  isSystem: boolean;
  createdAt: string;
}

// System Roles - Initial data with placeholder counts (will be updated dynamically)
const SYSTEM_ROLES: CustomRole[] = [
  {
    id: 'administrator',
    name: 'Administrator',
    description: ROLE_DESCRIPTIONS.administrator,
    permissions: DEFAULT_PERMISSIONS.administrator,
    userCount: 2,
    isSystem: true,
    createdAt: '2026-02-05T00:00:00Z',
  },
  {
    id: 'user',
    name: 'User',
    description: ROLE_DESCRIPTIONS.user,
    permissions: DEFAULT_PERMISSIONS.user,
    userCount: 15,
    isSystem: true,
    createdAt: '2026-02-05T00:00:00Z',
  },
];

// Permission Labels and Descriptions
const PERMISSION_LABELS: Record<keyof UserPermissions, string> = {
  manageUsers: 'Manage Users',
  manageCompanies: 'Manage Companies',
  manageIntegrations: 'Manage Integrations',
  configureAutomation: 'Configure Automation',
  configureAI: 'Configure AI',
  viewReports: 'View Reports',
  manageAccounts: 'Manage Accounts',
  sendCommunications: 'Send Communications',
  logActivities: 'Log Activities',
  viewPayments: 'View Payments',
  allocatePayments: 'Allocate Payments',
  reverseAllocations: 'Reverse Allocations',
  customerAccountsView: 'Customer Accounts View',
  customerAccountsEdit: 'Customer Accounts Edit',
  customerAccountsReassign: 'Customer Accounts Reassign',
  collectionCasesView: 'Collection Cases View',
  collectionCasesCreate: 'Collection Cases Create',
  collectionCasesEdit: 'Collection Cases Edit',
  collectionCasesDelete: 'Collection Cases Delete',
  collectionCasesReassign: 'Collection Cases Reassign',
  promiseToPayView: 'Promise to Pay View',
  promiseToPayCreate: 'Promise to Pay Create',
  promiseToPayEdit: 'Promise to Pay Edit',
  promiseToPayDelete: 'Promise to Pay Delete',
  promiseToPayReassign: 'Promise to Pay Reassign',
  viewCreditControlDashboard: 'View Credit Control Dashboard',
  viewCollectionsDashboard: 'View Collections Dashboard',
  viewDisputesDashboard: 'View Disputes Dashboard',
  viewAgeingAnalysis: 'View Ageing Analysis',
  viewMultiCompanyDashboard: 'View Multi-Company Dashboard',
  accessManagerViews: 'Access Manager Views',
  adminManageUsersView: 'Admin: Manage Users - View',
  adminManageUsersCreate: 'Admin: Manage Users - Create',
  adminManageUsersEdit: 'Admin: Manage Users - Edit',
  adminManageUsersDelete: 'Admin: Manage Users - Delete',
  adminManageCompaniesView: 'Admin: Manage Companies - View',
  adminManageCompaniesCreate: 'Admin: Manage Companies - Create',
  adminManageCompaniesEdit: 'Admin: Manage Companies - Edit',
  adminManageCompaniesDelete: 'Admin: Manage Companies - Delete',
  adminManageGeneralSettingsView: 'Admin: Manage General Settings - View',
  adminManageGeneralSettingsCreate: 'Admin: Manage General Settings - Create',
  adminManageGeneralSettingsEdit: 'Admin: Manage General Settings - Edit',
  adminManageGeneralSettingsDelete: 'Admin: Manage General Settings - Delete',
  adminManageIntegrationsView: 'Admin: Manage Integrations - View',
  adminManageIntegrationsCreate: 'Admin: Manage Integrations - Create',
  adminManageIntegrationsEdit: 'Admin: Manage Integrations - Edit',
  adminManageIntegrationsDelete: 'Admin: Manage Integrations - Delete',
  adminManageTemplatesView: 'Admin: Manage Templates - View',
  adminManageTemplatesCreate: 'Admin: Manage Templates - Create',
  adminManageTemplatesEdit: 'Admin: Manage Templates - Edit',
  adminManageTemplatesDelete: 'Admin: Manage Templates - Delete',
  adminManageCollectionStrategiesView: 'Admin: Manage Collection Strategies - View',
  adminManageCollectionStrategiesCreate: 'Admin: Manage Collection Strategies - Create',
  adminManageCollectionStrategiesEdit: 'Admin: Manage Collection Strategies - Edit',
  adminManageCollectionStrategiesDelete: 'Admin: Manage Collection Strategies - Delete',
  adminManageDisputeTypesView: 'Admin: Manage Dispute Types - View',
  adminManageDisputeTypesCreate: 'Admin: Manage Dispute Types - Create',
  adminManageDisputeTypesEdit: 'Admin: Manage Dispute Types - Edit',
  adminManageDisputeTypesDelete: 'Admin: Manage Dispute Types - Delete',
  adminConfigureAIView: 'Admin: Configure AI - View',
  adminConfigureAICreate: 'Admin: Configure AI - Create',
  adminConfigureAIEdit: 'Admin: Configure AI - Edit',
  adminConfigureAIDelete: 'Admin: Configure AI - Delete',
};

const PERMISSION_DESCRIPTIONS: Record<keyof UserPermissions, string> = {
  manageUsers: 'Create, edit, and delete user accounts',
  manageCompanies: 'Manage company settings and configurations',
  manageIntegrations: 'Configure and manage system integrations',
  configureAutomation: 'Set up and modify automation rules',
  configureAI: 'Configure AI and machine learning settings',
  viewReports: 'Access and view system reports',
  manageAccounts: 'Manage customer accounts and data',
  sendCommunications: 'Send emails and communications to customers',
  logActivities: 'Log activities and interactions',
  viewPayments: 'View payment records and transaction history',
  allocatePayments: 'Allocate payments to invoices',
  reverseAllocations: 'Reverse previously allocated payments',
  customerAccountsView: 'View customer accounts',
  customerAccountsEdit: 'Edit customer accounts',
  customerAccountsReassign: 'Reassign customer accounts',
  collectionCasesView: 'View collection cases',
  collectionCasesCreate: 'Create collection cases',
  collectionCasesEdit: 'Edit collection cases',
  collectionCasesDelete: 'Delete collection cases',
  collectionCasesReassign: 'Reassign collection cases',
  promiseToPayView: 'View promises to pay',
  promiseToPayCreate: 'Create promises to pay',
  promiseToPayEdit: 'Edit promises to pay',
  promiseToPayDelete: 'Delete promises to pay',
  promiseToPayReassign: 'Reassign promises to pay',
  viewCreditControlDashboard: 'Access to main credit control overview and metrics',
  viewCollectionsDashboard: 'Access to collections queue and team performance metrics',
  viewDisputesDashboard: 'Access to dispute tracking and analytics',
  viewAgeingAnalysis: 'Access to accounts receivable ageing reports',
  viewMultiCompanyDashboard: 'Access to consolidated multi-company view',
  accessManagerViews: 'Can switch between user views and access manager-level functionality',
  adminManageUsersView: 'View user account management',
  adminManageUsersCreate: 'Create new user accounts',
  adminManageUsersEdit: 'Edit existing user accounts',
  adminManageUsersDelete: 'Delete user accounts',
  adminManageCompaniesView: 'View company settings',
  adminManageCompaniesCreate: 'Create new companies',
  adminManageCompaniesEdit: 'Edit company configurations',
  adminManageCompaniesDelete: 'Delete companies',
  adminManageGeneralSettingsView: 'View general system settings',
  adminManageGeneralSettingsCreate: 'Create general settings',
  adminManageGeneralSettingsEdit: 'Edit general settings',
  adminManageGeneralSettingsDelete: 'Delete general settings',
  adminManageIntegrationsView: 'View system integrations',
  adminManageIntegrationsCreate: 'Create new integrations',
  adminManageIntegrationsEdit: 'Edit integration configurations',
  adminManageIntegrationsDelete: 'Delete integrations',
  adminManageTemplatesView: 'View communication templates',
  adminManageTemplatesCreate: 'Create new templates',
  adminManageTemplatesEdit: 'Edit existing templates',
  adminManageTemplatesDelete: 'Delete templates',
  adminManageCollectionStrategiesView: 'View collection strategies',
  adminManageCollectionStrategiesCreate: 'Create new strategies',
  adminManageCollectionStrategiesEdit: 'Edit strategy configurations',
  adminManageCollectionStrategiesDelete: 'Delete strategies',
  adminManageDisputeTypesView: 'View dispute type definitions',
  adminManageDisputeTypesCreate: 'Create new dispute types',
  adminManageDisputeTypesEdit: 'Edit dispute type definitions',
  adminManageDisputeTypesDelete: 'Delete dispute types',
  adminConfigureAIView: 'View AI configuration settings',
  adminConfigureAICreate: 'Create AI configurations',
  adminConfigureAIEdit: 'Edit AI settings',
  adminConfigureAIDelete: 'Delete AI configurations',
};

export function UsersTabsPage() {
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Pagination + filter state
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    role: 'all',
    status: 'active',
    sortBy: 'name_asc',
  });

  // Debounce search to avoid a request per keystroke
  const debouncedSearch = useDebouncedValue(filters.search, 300);

  const usersListParams: UsersListParams = {
    page: currentPage,
    pageSize: 20,
    search: debouncedSearch,
    role: filters.role,
    status: filters.status,
    sortBy: filters.sortBy,
  };

  // Shared data hooks
  const { data: usersResult, isLoading: isUsersLoading } = useUsersListPaginated(usersListParams);
  const updateUserMutation = useUpdateUser();
  const { apiPost } = useApi();

  const users = usersResult?.data ?? [];
  const pagination = usersResult?.pagination;

  // Dialog states for User Management
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [manageIntegrationsDialogOpen, setManageIntegrationsDialogOpen] = useState(false);
  const [manageCompaniesDialogOpen, setManageCompaniesDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Roles & Permissions State
  const [selectedRole, setSelectedRole] = useState<CustomRole | null>(null);
  const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState(false);
  const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);
  const [cannotDeleteDialogOpen, setCannotDeleteDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState<Partial<CustomRole>>({
    name: '',
    description: '',
    permissions: DEFAULT_PERMISSIONS.view_only,
  });

  // Reset to page 1 when filters change
  const updateFilters = (newFilters: Partial<UserFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  };

  const filteredRoles = SYSTEM_ROLES;

  // User Management Handlers
  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setViewDialogOpen(true);
  };

  const handleManageIntegrations = (user: User) => {
    setSelectedUser(user);
    setManageIntegrationsDialogOpen(true);
  };

  const handleManageCompanies = (user: User) => {
    setSelectedUser(user);
    setManageCompaniesDialogOpen(true);
  };

  const handleSaveIntegration = async (
    userId: string,
    evoUserId: string,
    externalFirstName: string,
    externalLastName: string,
  ) => {
    try {
      await apiPost(`/api/v1/user/${userId}`, {
        evoUserId: evoUserId || null,
        externalFirstName: externalFirstName || null,
        externalLastName: externalLastName || null,
      });

      queryClient.invalidateQueries({ queryKey: ['settings-users'] });
      toast.success('User integration updated successfully');
    } catch (error) {
      console.error('Error saving user integration:', error);
      toast.error('Failed to save user integration');
    }
  };

  const handleUpdateUserRole = async (userId: string, newRole: UserRole) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;

    try {
      await updateUserMutation.mutateAsync({
        userId,
        data: {
          ...user,
          role: newRole,
          permissions: DEFAULT_PERMISSIONS[newRole],
        },
      });

      toast.success(`User role updated to ${newRole}`);
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
      throw error;
    }
  };

  // Roles & Permissions Handlers
  const handleViewRole = (role: CustomRole) => {
    navigate(`/settings/users/roles/${role.id}`);
  };

  const handleCreateRole = () => {
    if (!newRole.name || !newRole.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    toast.success('Custom roles feature coming soon');
    setCreateRoleDialogOpen(false);
    setNewRole({
      name: '',
      description: '',
      permissions: DEFAULT_PERMISSIONS.view_only,
    });
  };

  const handleDeleteRoleClick = (role: CustomRole) => {
    setSelectedRole(role);

    // Check if role is a system role
    if (role.isSystem) {
      toast.error('System roles cannot be deleted');
      return;
    }

    // Check if role has users assigned
    if (role.userCount > 0) {
      setCannotDeleteDialogOpen(true);
    } else {
      setDeleteRoleDialogOpen(true);
    }
  };

  const handleDeleteRole = () => {
    if (!selectedRole) return;

    if (selectedRole.isSystem) {
      toast.error('Cannot delete system roles');
      return;
    }

    if (selectedRole.userCount > 0) {
      toast.error('Cannot delete role with assigned users');
      return;
    }

    // Create audit log entry before deletion
    console.log(`Audit: Role '${selectedRole.name}' deleted by current user`);

    toast.success(`Role '${selectedRole.name}' deleted successfully`);
    setDeleteRoleDialogOpen(false);
    setSelectedRole(null);
  };

  const handleViewUsersForRole = () => {
    if (!selectedRole) return;

    // Navigate to Users tab and filter by this role
    setCannotDeleteDialogOpen(false);
    navigate(`/settings/users/roles/${selectedRole.id}`, {
      state: { tab: 'users' },
    });
  };

  const handleDuplicateRole = (role: CustomRole) => {
    // Generate a unique name for the duplicated role
    let duplicateName = `${role.name} (Copy)`;
    let counter = 2;

    // Check if a role with this name already exists
    while (filteredRoles.some((r) => r.name === duplicateName)) {
      duplicateName = `${role.name} (Copy ${counter})`;
      counter++;
    }

    // Create a new role ID (in real app, this would be generated by backend)
    const newRoleId = `custom_${Date.now()}`;

    // Navigate to the create role page with the duplicated role data
    navigate(`/settings/users/roles-permissions/duplicate/${role.id}`, {
      state: {
        duplicateFrom: role,
        newRoleName: duplicateName,
      },
    });
  };

  if (isAdminLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <output
          aria-label="Loading"
          className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        />
      </div>
    );
  }
  if (!isAdmin) return <AdminOnlyEmptyState />;

  return (
    <div className="p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">Users</h1>
            <p className="text-muted-foreground mt-2">
              Manage user accounts, roles, permissions, and monitor activity across your organization
            </p>
          </div>
          <HelpSupportButton screenId="settings-user-management" />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="management" className="space-y-6">
          <TabsList>
            <TabsTrigger value="management">User Management</TabsTrigger>
            <TabsTrigger value="roles">Roles & Permissions</TabsTrigger>
            {/* <TabsTrigger value="activity">User Activity Log</TabsTrigger> */}
          </TabsList>

          {/* User Management Tab */}
          <TabsContent value="management" className="space-y-6">
            {/* Actions Bar */}
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={filters.search}
                  onChange={(e) => updateFilters({ search: e.target.value })}
                  className="pl-9"
                />
              </div>

              {/* Role Filter */}
              <Select
                value={filters.role}
                onValueChange={(value) => updateFilters({ role: value as UserRole | 'all' })}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="administrator">Administrator</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select
                value={filters.status}
                onValueChange={(value) => updateFilters({ status: value as UserStatus | 'all' })}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>

              {/* Sort */}
              <Select
                value={filters.sortBy}
                onValueChange={(value) => updateFilters({ sortBy: value as UserFilters['sortBy'] })}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name_asc">Name A-Z</SelectItem>
                  <SelectItem value="name_desc">Name Z-A</SelectItem>
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="role">Role</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Results Count */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {pagination
                  ? `Showing ${Math.min((currentPage - 1) * pagination.pageSize + 1, pagination.totalItems)}–${Math.min(currentPage * pagination.pageSize, pagination.totalItems)} of ${pagination.totalItems} users`
                  : ''}
              </p>
            </div>

            {/* Users Table */}
            <UsersTable
              users={users}
              isLoading={isUsersLoading}
              onViewUser={handleViewUser}
              onManageIntegrations={handleManageIntegrations}
              onManageCompanies={handleManageCompanies}
              onUpdateUserRole={handleUpdateUserRole}
              canManageUsers={true}
            />

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => p - 1)}
                  disabled={!pagination.hasPreviousPage}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {getPageNumbers(currentPage, pagination.totalPages).map((page, idx) =>
                  typeof page === 'number' ? (
                    <Button
                      key={page}
                      variant={page === currentPage ? 'default' : 'outline'}
                      size="sm"
                      className="w-9"
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Button>
                  ) : (
                    <span key={`${page}-${idx}`} className="w-9 text-center text-sm text-muted-foreground">
                      &hellip;
                    </span>
                  ),
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={!pagination.hasNextPage}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Roles & Permissions Tab */}
          <TabsContent value="roles" className="space-y-6">
            {/* Roles Table */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Role Name</TableHead>
                      <TableHead>Role Type</TableHead>
                      <TableHead className="text-center">Users Assigned</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Modified</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRoles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12">
                          <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                          <h3 className="text-lg font-semibold mb-2">No roles found</h3>
                          <p className="text-muted-foreground">Create a new role to get started</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRoles.map((role) => (
                        <TableRow key={role.id} className="hover:bg-muted/50 cursor-pointer">
                          <TableCell className="font-bold" onClick={() => handleViewRole(role)}>
                            {role.name}
                          </TableCell>
                          <TableCell>
                            {role.isSystem ? (
                              <Badge variant="secondary" className="bg-gray-100 text-gray-700 hover:bg-gray-100">
                                Platform Role
                              </Badge>
                            ) : (
                              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Custom Role</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">{role.userCount}</TableCell>
                          <TableCell>
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>
                          </TableCell>
                          <TableCell>05 Feb 2026</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewRole(role);
                              }}
                              title="View role"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Activity Log Tab — hidden for now */}
        </Tabs>

        {/* User Management Dialogs */}
        <ViewUserDialog open={viewDialogOpen} onOpenChange={setViewDialogOpen} user={selectedUser} />

        <ManageUserIntegrationsDialog
          open={manageIntegrationsDialogOpen}
          onOpenChange={setManageIntegrationsDialogOpen}
          user={selectedUser}
          onSave={handleSaveIntegration}
        />

        <ManageUserCompaniesDialog
          open={manageCompaniesDialogOpen}
          onOpenChange={setManageCompaniesDialogOpen}
          user={selectedUser}
        />

        {/* Roles & Permissions Dialogs */}
        <Dialog open={createRoleDialogOpen} onOpenChange={setCreateRoleDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedRole ? 'Edit Role' : 'Create New Role'}</DialogTitle>
              <DialogDescription>Define a custom role with specific permissions</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="role-name">Role Name *</Label>
                <Input
                  id="role-name"
                  placeholder="e.g., Collection Manager"
                  value={newRole.name}
                  onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role-description">Description *</Label>
                <Input
                  id="role-description"
                  placeholder="Describe the role's purpose and responsibilities"
                  value={newRole.description}
                  onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                />
              </div>

              <div className="space-y-4">
                <Label>Permissions</Label>
                <div className="space-y-3 border rounded-lg p-4">
                  {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                    <div key={key} className="flex items-start justify-between gap-4">
                      <div className="space-y-0.5">
                        <Label htmlFor={`perm-${key}`} className="font-medium">
                          {label}
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          {PERMISSION_DESCRIPTIONS[key as keyof UserPermissions]}
                        </p>
                      </div>
                      <Switch
                        id={`perm-${key}`}
                        checked={
                          typeof newRole.permissions?.[key as keyof UserPermissions] === 'boolean'
                            ? (newRole.permissions?.[key as keyof UserPermissions] as boolean)
                            : false
                        }
                        onCheckedChange={(checked) =>
                          setNewRole({
                            ...newRole,
                            permissions: {
                              ...newRole.permissions!,
                              [key]: checked,
                            },
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateRoleDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateRole}>{selectedRole ? 'Save Changes' : 'Create Role'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Role Confirmation Dialog - For roles with NO users */}
        <Dialog open={deleteRoleDialogOpen} onOpenChange={setDeleteRoleDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Role</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete '{selectedRole?.name}'? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteRoleDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteRole}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cannot Delete Dialog - For roles WITH users assigned */}
        <Dialog open={cannotDeleteDialogOpen} onOpenChange={setCannotDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                </div>
                <DialogTitle>Cannot Delete Role</DialogTitle>
              </div>
              <DialogDescription className="space-y-3">
                <p>
                  This role cannot be deleted because it has {selectedRole?.userCount}{' '}
                  {selectedRole?.userCount === 1 ? 'user' : 'users'} assigned.
                </p>
                <p>Please reassign these users to another role before deleting.</p>
                {selectedRole && selectedRole.userCount > 0 && (
                  <div className="mt-4 rounded-lg bg-muted p-3">
                    <p className="text-sm font-medium mb-2">Assigned Users:</p>
                    <div className="text-sm text-muted-foreground">
                      {users
                        .filter((user) => user.role === selectedRole.id)
                        .slice(0, 5)
                        .map((user) => (
                          <div key={user.id} className="py-1">
                            • {user.firstName} {user.lastName} ({user.email})
                          </div>
                        ))}
                      {selectedRole.userCount > 5 && (
                        <div className="py-1 font-medium">and {selectedRole.userCount - 5} more...</div>
                      )}
                    </div>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setCannotDeleteDialogOpen(false)}>
                Close
              </Button>
              <Button onClick={handleViewUsersForRole}>View Users</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
